import { Injectable } from "@angular/core";
import { Http } from "@angular/http";
import { BaseService } from "./base.service";
import {
  DataGroup,
  DataGroupColumn,
  PatientDatum,
} from "../models/data-group.model";
import { Tag } from "../models/tag.model";
import * as _ from "underscore";
import { DataType } from "../models/data-type.model";
import { SearchFilters } from "../models/search-filters.model";
import { Search } from "../models/search.model";
import { Parameter } from "../home/search/search.componentOLD";
import { MappingConsolidation } from "../models/mapping-consolidation.model";
import { TopLevelFieldMapping } from "../models/top-level-field-mapping.model";
import { PatientSearchLookup } from "../models/data-search-lookup.model";
import { ImageSearchLookup } from "../models/data-search-lookup.model";
import { MetaDataMapping } from "../models/meta-data.model";
import { GeneticSearchLookup } from "../models/data-search-lookup.model";
import { DataSearchLookup } from "../models/data-search-lookup.model";

@Injectable()
export class SearchService extends BaseService {
  private localSearchFilters: SearchFilters;
  private _patientData: Parameter[];
  private _metaData: Parameter[];
  private _searchTags: Tag[];
  private _patientIds: string;
  private _topLevelFilters: { [filterName: string]: {} };
  private recentSearchResults: any;

  constructor(protected http: Http) {
    super(http);
    this._patientIds = null;
    this._patientData = [];
    this._metaData = [];
    this._searchTags = [];
    this._topLevelFilters = {
      mactelDiagnosis: {},
      study: {},
    };
  }

  async getDataGroups(): Promise<Array<DataGroup>> {
    return this.get("search/datagroups");
  }

  async getPatientDataGroups(): Promise<Array<MappingConsolidation>> {
    return this.get("search/patientDatagroups");
  }

  async getDataGroupFor(spreadsheetId: string): Promise<DataGroup> {
    return this.get("search/datagroups/" + spreadsheetId);
  }

  async getTopLevelGroups(): Promise<TopLevelFieldMapping[]> {
    return this.get("search/topLevelGroups");
  }

  async getImageMappings(): Promise<MetaDataMapping[]> {
    return this.get("search/getImageMappings");
  }

  async getGeneticMappings(): Promise<MetaDataMapping[]> {
    return this.get("search/getGeneticMappings");
  }

  async getLookupData(params: {
    type: string;
    start: number;
    count: number;
    columns: MetaDataMapping[] | TopLevelFieldMapping[];
    patientIds: string[];
    patientFilters: {
      spreadsheetId: string;
      columnId: string;
      values: string[];
    }[];
    imageFilters: {
      columnId: string;
      values: string[];
    }[];
    geneticFilters: {
      columnId: string;
      values: string[];
    }[];
    orderBy?: string | null;
    desc?: boolean;
  }): Promise<{
    rows: DataSearchLookup[];
    pagination: { total: number };
    filterValues: { [key: string]: string[] };
    patientTotal: number;
  }> {
    return this.post("search/getLookupTable", {
      type: params.type,
      start: params.start,
      count: params.count,
      columns: params.columns,
      patientIds: params.patientIds,
      patientFilters: params.patientFilters,
      imageFilters: params.imageFilters,
      geneticFilters: params.geneticFilters,
      orderBy: params.orderBy ? params.orderBy : null,
      desc: params.desc ? params.desc : false,
    });
  }

  setSearchParams(
    patientIds: string,
    patientData: Parameter[],
    metaData: Parameter[],
    searchTags: Tag[],
    topLevelFilters: { [filterName: string]: {} }
  ) {
    this._patientIds = patientIds;
    this._patientData = patientData;
    this._metaData = metaData;
    this._searchTags = searchTags;
    this._topLevelFilters = topLevelFilters;
  }
  get patientIds() {
    return this._patientIds;
  }
  get patientData() {
    return this._patientData;
  }
  get metaData() {
    return this._metaData;
  }
  get searchTags() {
    return this._searchTags;
  }
  get topLevelFilters() {
    return this._topLevelFilters;
  }

  public async executeSearch(): Promise<Search> {
    return this.post("search", {
      patientIds: this._patientIds,
      patientData: this._patientData,
      metaData: this._metaData,
      searchTags: this._searchTags,
      topLevelFilters: this._topLevelFilters,
    });
  }

  public async executeEmailSearch(): Promise<Search> {
    return this.post("search", {
      patientIds: this._patientIds,
      patientData: this._patientData,
      metaData: this._metaData,
      searchTags: this._searchTags,
      topLevelFilters: this._topLevelFilters,
      emailResults: true,
    });
  }

  // when executing a new search (not an existing saved search), store the search params locally to execute
  setLocalSearchData(filters: SearchFilters) {
    this.localSearchFilters = filters;
  }

  getPatientColumns(): Array<DataGroupColumn> {
    const columns: Array<DataGroupColumn> = [];
    for (const datum of this.localSearchFilters.patientData) {
      if (
        ["METABOLOMICS", "GENETICS"].includes(datum.dataGroup.spreadsheetId)
      ) {
        continue; // filter our genetic and metabolomic columns from patient table
      }
      // we don't want duplicate columns here
      if (!_.findWhere(columns, { columnId: datum.dataGroupColumn.columnId })) {
        columns.push({
          columnId: datum.dataGroupColumn.columnId,
          columnName: datum.dataGroupColumn.columnName,
          spreadsheetId: datum.dataGroup.spreadsheetId,
        });
      }
    }
    return columns;
  }

  getLocalSearchFilters(): SearchFilters {
    return this.localSearchFilters;
  }

  getDataTypeFilters(): Array<DataType> {
    return this.localSearchFilters.dataTypes;
  }

  async executeLocalSearch(): Promise<Search> {
    if (!this.localSearchFilters) {
      throw new Error("Local search data not found.");
    }
    return this.post("search", this.localSearchFilters);
  }

  async getSearchStatus(search: Search): Promise<Search> {
    return this.get("search/status/" + search.searchId);
  }

  async getSearchResults(search: Search): Promise<any> {
    return this.get("search/results/" + search.searchId);
  }

  saveSearchResults(searchResults: any): void {
    this.recentSearchResults = searchResults;
  }

  getRecentSearchResults(): any {
    return this.recentSearchResults;
  }

  isRecentSearchResults(): boolean {
    return this.recentSearchResults !== undefined;
  }

  invalidateSearchResults() {
    this.recentSearchResults = undefined;
  }

  clearSearch() {
    this.invalidateSearchResults();
    this._patientIds = null;
    this._patientData = [];
    this._metaData = [];
    this._searchTags = [];
    this._topLevelFilters = {
      mactelDiagnosis: {},
      study: {},
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/services/search.service.ts